import { T } from '../data/tokens';
import FlatIcon from './FlatIcon';
import Gallery from './Gallery';
import Accordion from './Accordion';

const accent  = '#0052CC';
const accentR = '0,82,204'; 
const SCREENS = [
  { label: 'Template Selection', src: 'images/doc-template-selection.png',       bg: '#EFF6FF', fg: '#1d4ed8' },
  { label: 'Data Field Inspector',      src: 'images/data auto-pull-data-field inspector.png',      bg: '#EFF8FF', fg: '#0369a1' },
  { label: 'Smart Template Builder',    src: 'images/smart-template-builder.png',      bg: '#F0FDF4', fg: '#15803d' },
  { label: 'Workflow Engine',     src: 'images/workflow-engine.png',     bg: '#FFF7ED', fg: '#c2410c' },
  { label: 'Compliance Review',     src: 'images/diffview-compliance-review.png',     bg: '#FFF7ED', fg: '#c2410c' },
  { label: 'Delivery Mode Publish',     src: 'images/delivery-mode-publish.png',     bg: '#FFF7ED', fg: '#c2410c' },
  
];

const IMPACT_GROUPS = [
  { groupLabel: '⏱ Time Savings', stats: [
    { num: '78%',  desc: 'Document Creation Faster', sub: '5–7 hrs → 1.5 hrs' },
    { num: '68%',  desc: 'Review Time Faster',        sub: '25 min → 8 min' },
    { num: '81%',  desc: 'Template Creation Faster',  sub: '4 hrs → 45 min' },
  ]},
  { groupLabel: 'Quality', groupIcon: 'check_circle', stats: [
    { num: '89%',  desc: 'Error Reduction',           sub: '7.2% → 0.8%' },
    { num: '0',    desc: 'Reissuances',                sub: 'Post-launch' },
    { num: '43%',  desc: 'More Compliance Errors Caught', sub: '' },
  ]},
  { groupLabel: 'Business', groupIcon: 'work', stats: [
    { num: '$2.3M', desc: 'Annual Cost Savings',      sub: '' },
    { num: '92%',   desc: 'Feature Adoption',         sub: 'Month 1' },
    { num: '81',    desc: 'SUS Score',                sub: 'Up from 52' },
  ]},
];

export default function CaseStudyCloud() {
  return (
    <div>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <div style={T.hero} className="cs-hero">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(${accentR},0.15) 1px,transparent 1px),linear-gradient(90deg,rgba(${accentR},0.15) 1px,transparent 1px)`, backgroundSize: '48px 48px', pointerEvents: 'none' }} />
        <div style={T.heroInner}>
          <span style={T.pill}>Financial Services · Enterprise Platform</span>
          <h2 style={T.heroTitle}>Cloud-Based Automated Publishing Platform</h2>
          <p style={T.heroSub}>DocuBilder- Redesigning the Future of FinTech</p>
          <p style={{ ...T.heroTagline, color: accent }}>78% Faster Document Creation &amp; $2.3M Annual Savings</p>
          <div style={T.resultBand}>
            <p style={T.resultText}>
              I led the UX transformation of a fragmented, manual reporting process into an <strong>automated publishing engine</strong>. By shifting the product strategy from <strong>"Black Box Automation"</strong> to <strong>"Transparent Control,"</strong> we reduced the document lifecycle from <strong>7 hours to 90 minutes</strong> while maintaining 100% compliance.
            </p>
          </div>
          <div style={T.metricsRow} className="cs-metrics">
            <div style={T.metric}><span style={T.metricNum}>78%</span><span style={T.metricLabel}>Faster Creation</span><span style={T.metricSub}>5–7 hrs → 1.5 hrs</span></div>
            <div style={T.metric}><span style={T.metricNum}>89%</span><span style={T.metricLabel}>Error Reduction</span><span style={T.metricSub}>7.2% → 0.8%</span></div>
            <div style={T.metric}><span style={T.metricNum}>$2.3M</span><span style={T.metricLabel}>Annual Savings</span><span style={T.metricSub}>Year one</span></div>
            <div style={T.metric}><span style={T.metricNum}>92%</span><span style={T.metricLabel}>Feature Adoption</span><span style={T.metricSub}>Month 1</span></div>
          </div>
        </div>
      </div>

      {/* ── Stats Strip ─────────────────────────────────────────────── */}
      <div style={T.detailStatsStrip} className="detail-stats-strip">
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Role</span><span style={T.detailStatCellValue}>Lead Product &amp; UX Designer</span></div>
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Duration</span><span style={T.detailStatCellValue}>18 Months</span></div>
        <div style={{ ...T.detailStatCell }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Team</span><span style={T.detailStatCellValue}>8-Person Product Team</span></div>
      </div>

      {/* ── Problem ─────────────────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="help" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Problem</p>
        <h3 style={T.sectionTitle}>A broken, manual process costing millions</h3>
        <p style={T.sectionIntro}>Financial services teams were spending 7+ hours manually assembling compliance reports — copy-pasting data across 500+ documents, with no audit trail and constant risk of human error. Investment Analysts didn't trust automation they couldn't verify, and compliance teams rejected any system that felt like a "black box."</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '1.25rem' }}>
          {[
            { v: '7 hrs',  l: 'per report cycle' },
            { v: '500+',   l: 'reports per year' },
            { v: '89%',    l: 'error-prone manual steps' },
          ].map((s) => (
            <div key={s.v} style={{ flex: 1, borderRadius: '10px', padding: '1rem 1.25rem', background: `rgba(${accentR},0.06)`, border: `1px solid rgba(${accentR},0.2)` }}>
              <div style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: '1.7rem', color: accent, lineHeight: 1, marginBottom: '4px' }}>{s.v}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', color: '#64748b', letterSpacing: '0.04em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Solution — 5 cards ──────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="rocket" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Final Solution</p>
        <h3 style={T.sectionTitle}>How it Works</h3>
        <p style={T.sectionIntro}>Before we dive into the process, here is the final system designed to bridge the gap between complex data and compliant design.</p>
        <div style={T.solCards}>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>01</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Data Field Inspector <span style={{ ...T.solTag, background: `rgba(${accentR},0.1)`, color: accent }}>Trust through Transparency</span></h4>
              <p style={T.solBody}>To solve user resistance to automation, I designed a hover-state inspector that shows the exact source, timestamp, and transformation logic for every data point.</p>
              <div style={{ ...T.solResult, color: accent, background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)` }}>The Result: Manual verification time dropped by 67%</div>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>02</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Smart Template Editor</h4>
              <p style={T.solBody}>A drag-and-drop builder with natural language conditional rules, allowing non-technical marketing teams to update 200+ templates in minutes rather than weeks.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>03</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Unified Review Dashboard</h4>
              <p style={T.solBody}>A Kanban-style hub that eliminates context-switching by merging compliance, data, and design reviews into a single collaborative view.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>04</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Intelligent Change Tracking</h4>
              <p style={T.solBody}>Side-by-side diff view highlighting only data/content changes, ignoring formatting noise.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>05</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Automated Workflow Engine</h4>
              <p style={T.solBody}>Rule-based routing with notifications, parallel approvals, and complete audit trails.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Gallery ─────────────────────────────────────────────────── */}
      <Gallery screens={SCREENS} accent={accent} />

      {/* ── Product Strategy ────────────────────────────────────────── */}
      <Accordion label="🎯 The Product Strategy" accent={accent}>
        <div style={{ ...T.section, background: '#0A0E27' }} className="cs-section">
          <h3 style={{ ...T.sectionTitle, color: '#fff' }}>Balancing Speed &amp; Trust</h3>
          <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.8)' }}>
            At a senior level, my role wasn't just to design screens — it was to solve the <strong>"Automation Paradox."</strong>
          </p>
          <div style={T.strategyGrid} className="cs-strategy-grid">
            <div style={T.strategyBlock}>
              <h4 style={T.strategyHeading}>The Strategic Pivot</h4>
              <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.72)', marginTop: '0.5rem', marginBottom: 0 }}>
                The original business goal was to <em>"automate everything."</em> However, my research revealed that Investment Analysts didn't trust a system they couldn't audit. I pivoted the strategy to <strong>"Assisted Automation."</strong>
              </p>
            </div>
            <div style={T.strategyBlock}>
              <h4 style={T.strategyHeading}>The Strategic Trade-off</h4>
              <div style={T.tradeoffItem}>
                <span style={T.tradeoffLabel}>The Problem</span>
                <span style={T.tradeoffText}>Power users wanted total customization; Business users wanted speed.</span>
              </div>
              <div style={{ ...T.tradeoffItem, marginTop: '0.9rem' }}>
                <span style={T.tradeoffLabel}>The Decision</span>
                <span style={T.tradeoffText}>I moved layout logic to a Global Design System layer — analysts stay on-brand automatically while designers focus on high-level strategy rather than fixing typos.</span>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── The Journey ─────────────────────────────────────────────── */}
      <Accordion label="🔍 The Journey" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>How We Got There</h3>
          <div style={T.journeyGrid} className="cs-journey-grid">

            {/* Challenge card */}
            <div style={T.journeyCard}>
              <div style={T.journeyIcon}><FlatIcon name="warning" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The Challenge &amp; Pain Points</h4>
              <p style={T.sectionIntro}>Analysts were spending 4+ hours daily manually moving data from Bloomberg into InDesign. One typo could result in massive compliance fines and lost client trust.</p>
              {[
                { q: '"I spend 4 hours copying data from Bloomberg into InDesign. One error and I lose client trust."',     who: '— Investment Analyst' },
                { q: '"I can\'t tell what changed between versions without comparing PDFs line by line."',                   who: '— Compliance Officer' },
                { q: '"Updating 200+ templates when branding changes takes 3 weeks."',                                      who: '— Marketing Operations Manager' },
              ].map((q) => (
                <div key={q.who} style={T.quote}>
                  <p style={T.quoteText}>{q.q}</p>
                  <cite style={{ ...T.quoteCite, color: accent }}>{q.who}</cite>
                </div>
              ))}
            </div>

            {/* Aha Moment card */}
            <div style={{ ...T.journeyCard, ...T.journeyCardAccent }}>
              <div style={T.journeyIcon}><FlatIcon name="lightbulb" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The "Aha!" Moment</h4>
              <p style={T.sectionIntro}>Through <strong>18 in-depth interviews</strong> and contextual inquiries, I discovered the core blocker: <strong>Risk.</strong></p>
              <p style={{ ...T.sectionIntro, marginTop: '0.75rem' }}>
                Users did not hate the work; they hated the <em>vulnerability</em> of not knowing if the automated data was correct. This insight led directly to the creation of the <strong>Data Field Inspector.</strong>
              </p>
            </div>
          </div>

          {/* Research Methods */}
          <h4 style={T.subsectionTitle}>Research Methods</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'mic', title: 'In-Depth Interviews',  desc: '18 interviews across 4 financial institutions' },
              { icon: 'visibility', title: 'Contextual Inquiry',   desc: 'Shadowed 12 users during actual document production' },
              { icon: 'bar_chart', title: 'Data Analysis',         desc: 'Analyzed 450+ support tickets from legacy systems' },
              { icon: 'book', title: 'Diary Studies',         desc: 'Tracked 8 participants over 2 weeks' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* The 4 Pillars of Insight */}
          <h4 style={T.subsectionTitle}>The 4 Pillars of Insight</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'search', label: 'Trust Over Speed',              text: "Users wouldn't adopt automation if they couldn't verify the 'black box' logic." },
              { icon: 'rule', label: 'The Control Paradox',           text: 'Users wanted automation, but they feared losing the ability to override it.' },
              { icon: 'hub', label: 'Context Switching Kills',        text: 'Toggling between 7 different tools was the primary cause of fatigue.' },
              { icon: 'globe', label: 'Asynchronous Collaboration',     text: 'Global teams needed clear status visibility over real-time chat.' },
            ].map((r) => (
              <div key={r.label} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.label}</strong>
                  <p style={T.researchDesc}>{r.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Design Principles */}
          <h4 style={T.subsectionTitle}>Design Principles</h4>
          <p style={{ ...T.sectionIntro, marginBottom: '1rem' }}>Based on the research, I established five guiding principles that governed every design decision:</p>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'visibility', label: 'Transparent by Default',    text: "Show the 'why' behind every automated number." },
              { icon: 'rule',       label: 'Forgiving and Reversible',  text: 'Every action must be undoable to reduce user anxiety.' },
              { icon: 'target',     label: 'Context over Clicks',        text: 'Bring the tools to the data, not the other way around.' },
              { icon: 'extension',  label: 'Progressive Disclosure',    text: 'Hide advanced features until they are needed to keep the UI clean.' },
              { icon: 'star',       label: 'Clarity over Cleverness',   text: 'Use simple, functional language over complex financial jargon.' },
            ].map((r) => (
              <div key={r.label} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.label}</strong>
                  <p style={T.researchDesc}>{r.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testing & Iteration */}
          <h4 style={T.subsectionTitle}>Testing &amp; Iteration — 5 Rounds</h4>
          <div style={T.iterTimeline} className="cs-iter-timeline">
            <div style={{ ...T.iterItem, background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.2)' }}>
              <span style={{ ...T.iterDot, background: '#ef4444' }} />
              <div>
                <strong style={T.iterLabel}>Iteration 1</strong>
                <p style={T.iterText}>Data Inspector hidden in a menu → <span style={{ color: '#ef4444', fontWeight: 600 }}>20% discoverability</span></p>
              </div>
            </div>
            <span style={T.iterArrow}>→</span>
            <div style={{ ...T.iterItem, background: 'rgba(34,197,94,0.07)', borderColor: 'rgba(34,197,94,0.25)' }}>
              <span style={{ ...T.iterDot, background: '#22c55e' }} />
              <div>
                <strong style={T.iterLabel}>Final Version</strong>
                <p style={T.iterText}>Progressive Disclosure hover-state, placed where the eye naturally rests → <span style={{ color: '#22c55e', fontWeight: 600 }}>85% discoverability</span></p>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Design Process ──────────────────────────────────────────── */}
      <Accordion label="✏️ Design Process" accent={accent}>
        <div style={{ ...T.section, background: '#fafaf8' }} className="cs-section">
          <h3 style={T.sectionTitle}>The Messy Middle</h3>
          <p style={T.sectionIntro}>Before the polished final system, there were wrong turns, rejected directions, and one internal conflict that nearly killed the most important feature.</p>

          {/* What I Believed — red left-border callout box */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid #0052CC`, borderRadius: '10px', padding: '1.5rem 1.75rem', marginBottom: '1.5rem', marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#0052CC', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: 600 }}>What I Believed at the Start</p>
            <p style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.45, marginBottom: '0.75rem' }}>
              "If we automate everything and make it fast, analysts will love it."
            </p>
            <p style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
              This was the brief, the stakeholder expectation, and my starting assumption. I began designing a fully automated pipeline — data pulled from Bloomberg, formatted automatically, ready to send. It was technically impressive. Nobody wanted to use it.
            </p>
          </div>

          {/* 3 Directions — 3-col grid matching other case studies */}
          <h4 style={T.subsectionTitle}>3 Directions Explored — Only One Survived</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }} className="cs-three-col">
            {[
              { dot: '#ef4444', border: '#fecaca', label: 'DIRECTION A — REJECTED', title: 'Full Black Box Automation',          body: 'Complete automation, no data source visibility. Fast, clean — and completely distrusted. Round 1 testing killed it. Users refused to sign off on numbers they could not verify.' },
              { dot: '#f97316', border: '#fed7aa', label: 'DIRECTION B — REJECTED', title: 'Manual with Automation Suggestions',  body: 'Automation as a passive assistant, humans do everything. Users felt safer but hated the speed. It was barely faster than the old process. Rejected in round 2 — defeated the business case entirely.' },
              { dot: '#22c55e', border: '#bbf7d0', label: 'DIRECTION C — SHIPPED',  title: 'Transparent Assisted Automation',    body: 'Automation does the work. The Data Field Inspector shows exactly where every number came from. Users stay in control without doing the manual labour. Passed every round of testing.' },
            ].map((v) => (
              <div key={v.label} style={{ background: '#fff', border: `1px solid ${v.border}`, borderRadius: '10px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: v.dot, display: 'inline-block' }} />
                  <strong style={{ fontSize: '0.78rem', color: v.dot, fontFamily: "'DM Mono', monospace", letterSpacing: '0.04em' }}>{v.label}</strong>
                </div>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.5rem' }}>{v.title}</p>
                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{v.body}</p>
              </div>
            ))}
          </div>

          {/* Data Field Inspector got built — 4-version grid */}
          <h4 style={{ ...T.subsectionTitle, marginTop: '1.5rem' }}>How the Data Field Inspector Actually Got Built</h4>
          <p style={{ ...T.sectionIntro, marginBottom: '1.25rem' }}>The inspector was the most iterated feature in the project — 4 distinct forms before it worked.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem', marginBottom: '2rem' }} className="cs-four-col">
            {[
              { v: 'V1', label: 'Buried in sidebar menu',           result: '12% found it',  color: '#ef4444', note: 'Nobody opened the menu during normal workflow' },
              { v: 'V2', label: 'Floating tooltip on click',        result: '34% found it',  color: '#f97316', note: 'Users did not know data cells were clickable' },
              { v: 'V3', label: 'Persistent icon on hover',         result: '67% found it',  color: '#eab308', note: 'Close — but icon competed with data visually' },
              { v: 'V4', label: 'Progressive disclosure at eye-line',result: '85% found it', color: '#22c55e', note: 'Appears exactly where the eye rests on the document' },
            ].map((e) => (
              <div key={e.v} style={{ background: '#fff', border: `1px solid ${e.color}44`, borderTop: `3px solid ${e.color}`, borderRadius: '8px', padding: '1rem' }}>
                <div style={{ fontSize: '0.68rem', fontFamily: "'DM Mono', monospace", color: e.color, fontWeight: 700, marginBottom: '0.4rem', letterSpacing: '0.06em' }}>{e.v}</div>
                <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.35rem', lineHeight: 1.35 }}>{e.label}</p>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: e.color, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.4rem' }}>{e.result}</p>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.55, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{e.note}</p>
              </div>
            ))}
          </div>

          {/* The Decision — dark #0A0E27 box */}
          <div style={{ background: '#0A0E27', borderRadius: '12px', padding: '1.75rem 2rem', marginBottom: '1.5rem', marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: `rgba(${accentR},0.9)`, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem', fontWeight: 600 }}>The Decision That Almost Did Not Happen</p>
            <p style={{ fontSize: '1.02rem', fontWeight: 700, color: '#fff', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, marginBottom: '1rem' }}>
              The compliance team wanted the inspector removed entirely. They argued it added visual noise to documents that needed to look clean for client delivery.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              This was a legitimate concern — the inspector was visible during document editing, and compliance officers were worried clients might see draft annotations. I had two options: remove the feature, or solve the real problem.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              I proposed a single toggle: <strong style={{ color: `rgba(${accentR},0.9)` }}>Edit Mode</strong> vs <strong style={{ color: `rgba(${accentR},0.9)` }}>Delivery Mode</strong>. In Edit Mode the inspector is fully visible. In Delivery Mode it disappears completely — the document looks exactly as the client receives it.
            </p>
            <div style={{ background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)`, borderRadius: '8px', padding: '1rem 1.25rem' }}>
              <p style={{ fontSize: '0.88rem', color: `rgba(${accentR},0.9)`, lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                This single decision unblocked the feature, resolved the compliance objection, and became one of the most praised aspects of the final product. The toggle was never in the original scope.
              </p>
            </div>
          </div>

          {/* Documented principle */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}><FlatIcon name="rule" size={22} color="#94a3b8" /></span>
            <div>
              <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.4rem', fontWeight: 600 }}>Documented in Figma Component Notes</p>
              <p style={{ fontSize: '0.95rem', color: '#1A1A1A', lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                "Rule: Every automated data point must show its source on hover. No exceptions. If the data cannot be traced, it should not be automated."
              </p>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: "'DM Sans', sans-serif", margin: '0.5rem 0 0' }}>
                This principle was adopted as an official product rule and applied to every subsequent feature built on the automation engine.
              </p>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Business Impact ─────────────────────────────────────────── */}
      <Accordion label="📈 Business Impact" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>Measurable Results</h3>
          <div style={T.impactGroups}>
            {IMPACT_GROUPS.map((group) => (
              <div key={group.groupLabel} style={T.impactGroup}>
                <div style={{...T.impactGroupLabel, display:"flex", alignItems:"center", gap:"0.5rem"}}><FlatIcon name={group.groupIcon || "bolt"} size={14} color="currentColor" />{group.groupLabel}</div>
                <div style={T.impactStats} className="cs-impact-stats">
                  {group.stats.map((s) => (
                    <div key={s.desc} style={T.impactStat}>
                      <span style={T.impactNum}>{s.num}</span>
                      <span style={T.impactDesc}>
                        {s.desc}
                        {s.sub && <small style={{ display: 'block', fontSize: '0.68rem', color: '#94a3b8', marginTop: '2px' }}>{s.sub}</small>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── User Feedback ───────────────────────────────────────────── */}
      <Accordion label="💬 User Feedback" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>In Their Own Words</h3>
          <div style={T.quotesGrid} className="cs-quotes-grid">
            {[
              '"I finally have time to analyze data instead of copying it around. The data inspector changed everything."',
              '"Review time dropped from 25 minutes to 8. I catch more errors because I\'m not distracted by formatting noise."',
              '"We launched 8 new funds last quarter without adding staff."',
            ].map((q, i) => (
              <div key={i} style={T.userQuote}>{q}</div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── Key Learnings ───────────────────────────────────────────── */}
      <Accordion label="💡 Key Learnings &amp; Reflection" accent={accent}>
        <div style={{ ...T.section, background: '#f8fafc' }} className="cs-section">
          <h3 style={T.sectionTitle}>What We Learned</h3>
          <div style={T.learningsGrid} className="cs-learnings-grid">
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: accent }}>What Worked</h4>
              <ul style={T.caseList}>
                {[
                  'Progressive disclosure served both novice and power users — basic/advanced modes hit the sweet spot',
                  '5 rounds of testing caught critical issues before they became expensive',
                  'Edit Mode / Delivery Mode resolved the compliance objection without compromising the feature',
                ].map((item) => (
                  <li key={item} style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}>
                    <FlatIcon name="check_circle" size={14} color={accent} style={{flexShrink:0,marginTop:"5px"}} />{item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: '#94a3b8' }}>What I Would Change</h4>
              <ul style={T.caseList}>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}>
                  <FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />
                  Involve compliance stakeholders in initial wireframing — early feedback could have saved <strong>3 weeks of rework</strong> on audit trail exports
                </li>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />Test with real data from day 1 (revealed 12 edge cases wed missed)</li>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />Plan for mobile use cases upfront</li>
              </ul>
            </div>
          </div>

          {/* Core Takeaway */}
          <div style={T.takeaway}>
            <FlatIcon name="star" size={24} color={accent} style={T.takeawayIcon} />
            <div>
              <strong style={T.takeawayTitle}>Core Takeaway</strong>
              <p style={T.takeawayText}>Enterprise UX is not just about aesthetics — it is about building trust in the systems people rely on for their livelihoods. Making automation transparent, controllable, and <span style={{ fontStyle: 'italic' }}>reversible</span> is what separates adoption from resistance.</p>
            </div>
          </div>
        </div>
      </Accordion>

    </div>
  );
}
